import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  AccountBalance as AccountBalanceIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const TermsPage: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    {
      icon: <GavelIcon />,
      title: t('terms.acceptance'),
      content: t('terms.acceptanceItems', { returnObjects: true }) as string[],
    },
    {
      icon: <AccountBalanceIcon />,
      title: t('terms.rightsObligations'),
      content: t('terms.rightsObligationsItems', { returnObjects: true }) as string[],
    },
    {
      icon: <PaymentIcon />,
      title: t('terms.payments'),
      content: t('terms.paymentsItems', { returnObjects: true }) as string[],
    },
    {
      icon: <SecurityIcon />,
      title: t('terms.security'),
      content: t('terms.securityItems', { returnObjects: true }) as string[],
    },
    {
      icon: <CancelIcon />,
      title: t('terms.termination'),
      content: t('terms.terminationItems', { returnObjects: true }) as string[],
    },
    {
      icon: <WarningIcon />,
      title: t('terms.liability'),
      content: t('terms.liabilityItems', { returnObjects: true }) as string[],
    },
  ];

  const prohibitedActivities = t('terms.prohibitedActivitiesItems', { returnObjects: true }) as string[];

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="terms" />
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
{t('terms.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 800,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
{t('terms.subtitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#a0a0a0',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
            }}
          >
{t('terms.lastUpdated')}: {new Date().toLocaleDateString('bg-BG')}
          </Typography>
        </Box>

        {/* Introduction */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(40, 160, 240, 0.3)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('terms.introduction')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('terms.introText1')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('terms.introText2')}
            </Typography>
          </CardContent>
        </Card>

        {/* Main Sections */}
        {sections.map((section, index) => (
          <Card
            key={index}
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: 4,
              mb: 4,
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    color: '#28A0F0',
                    mr: 2,
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: 32, md: 40 },
                    },
                  }}
                >
                  {section.icon}
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {section.title}
                </Typography>
              </Box>
              <List>
                {section.content.map((item, itemIndex) => (
                  <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#d0d0d0',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        ))}

        {/* Prohibited Activities */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 4,
            mb: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('terms.prohibitedActivities')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('terms.prohibitedActivitiesDesc')}
            </Typography>
            <List>
              {prohibitedActivities.map((activity, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#d0d0d0',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          lineHeight: 1.6,
                        }}
                      >
                        • {activity}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Dispute Resolution */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(40, 160, 240, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 4,
            mb: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('terms.disputeResolution')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 2,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('terms.disputeResolutionText1')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('terms.disputeResolutionText2')}
            </Typography>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('terms.contactTerms')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 2,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('terms.contactTermsText')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                Имейл: legal@workio.bg
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                Телефон: +359 888 123 456
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                Адрес: ул. Витоша 12, София 1000, България
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
{t('terms.termsChanges')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#a0a0a0',
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              lineHeight: 1.5,
            }}
          >
            {t('terms.termsChangesText')}
          </Typography>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default TermsPage;
